/**
 * WhatsApp 24-hour customer-service window.
 *
 * Mirrors smsEngine.isWithinSessionWindow (teamio-lambda-layer) — KEEP IN SYNC.
 * WhatsApp only allows free-form (non-template) messages within 24 hours of the
 * patient's last inbound message; outside that window an approved template is
 * required. The chat UI uses this to show a banner (and, later, gate the input).
 *
 * `lastInboundAtMs` is written on the conversation doc by the backend on every
 * inbound message. Absent / 0 => no inbound yet => window is closed.
 */
export const WHATSAPP_WINDOW_MS = 24 * 60 * 60 * 1000;

export function isWithinWhatsAppWindow(lastInboundAtMs, nowMs) {
  const last = Number(lastInboundAtMs);
  if (!last || Number.isNaN(last)) return false;
  return (nowMs - last) <= WHATSAPP_WINDOW_MS;
}

/** A loaded chat message is inbound (from the patient) if marked so or stored as received. */
export function isInboundMessage(m) {
  return !!(m && ((m.attributes && m.attributes.inbound) || m.status === 'received'));
}

/**
 * Channel of the most recent INBOUND message ('sms' | 'whatsapp'), or null if the
 * patient hasn't messaged. Drives the smart default for the reply-channel selector
 * (default to whatever the patient last used). Messages are chronological.
 */
export function lastInboundChannel(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isInboundMessage(messages[i])) return messages[i].channel || 'sms';
  }
  return null;
}

/**
 * Timestamp (ms) of the most recent inbound WHATSAPP message, or null. The WhatsApp
 * 24h window is measured from this specifically (an SMS arriving later must NOT
 * re-open the WhatsApp window). `tsNum` is the millis the chat mapper stamps.
 */
export function lastWhatsAppInboundMs(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (isInboundMessage(m) && (m.channel || 'sms') === 'whatsapp') {
      const n = Number(m.tsNum);
      return Number.isNaN(n) ? null : n;
    }
  }
  return null;
}

/**
 * The channel a Conversations reply goes out on.
 *
 *   - Site without WhatsApp      → 'sms' (nothing else exists).
 *   - WhatsApp-only office       → 'whatsapp', always. Outbound SMS is switched off
 *     (SMS_OUTBOUND_DISABLED), so neither the patient's last inbound channel (an inbound SMS
 *     still lands here) nor an earlier "SMS" pick may route a reply to SMS — the server would
 *     refuse it.
 *   - Otherwise                  → the agent's pick, else the patient's last inbound channel,
 *     else 'sms' (unchanged behaviour).
 */
export function resolveReplyChannel({ siteWhatsAppEnabled, smsOutboundOff, override, defaultChannel } = {}) {
  if (!siteWhatsAppEnabled) return 'sms';
  if (smsOutboundOff) return 'whatsapp';
  return override || defaultChannel || 'sms';
}

/**
 * What staff see when the 24-hour window has closed (Saumil, 2026-10-11): SAY SO, plainly, and block the
 * send. NEVER tell anyone to switch to SMS: an office may not have SMS at all (The Oral Care Centre /
 * Barbados), and no office is nudged to change channel. So the wording is the same for every office. The
 * old per-office variants (`smsOutboundOff`) are gone; callers may still pass the option, it is ignored.
 *
 * The notice mentions approved messages only when `hasApproved` (the button is on screen): the text must never
 * point at a control that is not there. The toast and the hint never mention them.
 */
const WINDOW_CLOSED_TITLE = "WhatsApp's 24-hour reply window has closed";
const WINDOW_CLOSED_BODY = "You can't send a free-form message to this patient until they message you on WhatsApp again.";

/** Added to the banner ONLY when the "Send an approved message" button is on screen beside it (step 4 part 2). */
const WINDOW_CLOSED_APPROVED_BODY = 'You can still send one of your approved messages.';

/**
 * The banner that replaces the message box while the window is closed. `hasApproved` is true only when the
 * office has an approved message staff can send, i.e. when the button the sentence points at is really there.
 */
export function whatsappWindowClosedNotice({ hasApproved = false } = {}) {
  return { title: WINDOW_CLOSED_TITLE, body: hasApproved ? `${WINDOW_CLOSED_BODY} ${WINDOW_CLOSED_APPROVED_BODY}` : WINDOW_CLOSED_BODY };
}

/** The short line in the "Reply via" bar. The big notice above carries the explanation. */
export function whatsappWindowClosedHint() {
  return '24-hour reply window closed';
}

/** A defence toast if a send still reaches the handler with the window closed (the message box is hidden). */
export function whatsappWindowClosedToast() {
  return `${WINDOW_CLOSED_TITLE}. ${WINDOW_CLOSED_BODY}`;
}

/**
 * Is the message box blocked? Only a WhatsApp reply with the window closed: an SMS reply is never held by
 * the WhatsApp window, and unknown input never blocks by accident. `messagesLoaded: false` (the thread is
 * still loading, so "no inbound message yet" is not a fact) never blocks, or the banner would flash up on
 * every open.
 */
export function isWhatsAppComposerBlocked({ replyChannel, windowOpen, messagesLoaded = true } = {}) {
  return replyChannel === 'whatsapp' && windowOpen === false && messagesLoaded !== false;
}
